import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button, Input, Label, Textarea } from '@phrasewright/ui';
import { useCreateFileMutation } from '@/store/api/unifiedStorageApi';
import { useToast } from '@/hooks/useToast';
import { useAppSelector } from '@/store/hooks';
import { FileText, Hash } from 'lucide-react';

interface CreateFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultType?: 'document' | 'phrase';
}

export function CreateFileDialog({
  open,
  onOpenChange,
  defaultType = 'document',
}: CreateFileDialogProps) {
  const [createFile, { isLoading }] = useCreateFileMutation();
  const { showSuccess, showError } = useToast();
  const { currentProject } = useAppSelector((state) => state.project);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState<'document' | 'phrase'>(defaultType);
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');

  const handleCreate = async () => {
    if (!fileName.trim()) {
      showError('File name is required');
      return;
    }

    try {
      await createFile({
        name: fileName.trim(),
        content: content.trim(),
        type: fileType,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        projectId: currentProject?.id,
        metadata: {
          createdBy: 'user',
          source: 'manual',
        },
      }).unwrap();

      showSuccess(
        'File created',
        `${fileType === 'document' ? 'Document' : 'Phrase'} "${fileName.trim()}" has been created`
      );

      // Reset form and close dialog
      setFileName('');
      setContent('');
      setTags('');
      onOpenChange(false);
    } catch (error) {
      showError(
        'Failed to create file',
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New File</DialogTitle>
          <DialogDescription>
            Create a new document or phrase to start organizing your content.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="fileName">Name</Label>
            <Input
              id="fileName"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="Enter file name..."
              autoFocus
            />
          </div>

          <div className="grid gap-2">
            <Label>Type</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="fileType"
                  value="document"
                  checked={fileType === 'document'}
                  onChange={(e) =>
                    setFileType(e.target.value as 'document' | 'phrase')
                  }
                />
                <FileText className="h-4 w-4" />
                Document
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="fileType"
                  value="phrase"
                  checked={fileType === 'phrase'}
                  onChange={(e) =>
                    setFileType(e.target.value as 'document' | 'phrase')
                  }
                />
                <Hash className="h-4 w-4" />
                Phrase
              </label>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="content">Initial Content (Optional)</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={
                fileType === 'document'
                  ? 'Start writing your document...'
                  : 'Enter your phrase...'
              }
              rows={4}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="tags">Tags (Optional)</Label>
            <Input
              id="tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Enter tags separated by commas..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!fileName.trim() || isLoading}
          >
            {isLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
